import React, { useState, useEffect } from 'react';
import eventService from '../../services/eventService';

const ParticipantsList = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadEvents();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'Unbekannt';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Ungültiges Datum';
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const loadEvents = async () => {
    try {
      setLoading(true);
      const response = await eventService.getEventsAdmin();
      if (response.data.success) {
        setEvents(response.data.events);
      } else {
        setError('Fehler beim Laden der Events');
      }
    } catch (error) {
      console.error('Error loading events:', error);
      setError('Fehler beim Laden der Events');
    } finally {
      setLoading(false);
    }
  };

  const exportParticipants = (eventDate, eventName) => {
    const csvContent = [
      ['Veranstaltung', 'Termin', 'Name', 'Status'],
      ...eventDate.participants.map(p => [
        eventName,
        formatDate(eventDate.date),
        p.user?.username || 'Unbekannt',
        'Bestätigt'
      ]),
      ...eventDate.waitlist.map(w => [
        eventName,
        formatDate(eventDate.date),
        w.user?.username || 'Unbekannt',
        'Warteliste'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `teilnehmer_${eventName}_${formatDate(eventDate.date).replace(/\//g, '-')}.csv`;
    link.click();
  };

  if (loading) {
    return (
      <div className="text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Laden...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  return (
    <div className="participants-list">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Teilnehmerlisten</h3>
      </div>

      {events.length === 0 ? (
        <div className="alert alert-info">
          <i className="bi bi-info-circle me-2"></i>
          Keine Events vorhanden.
        </div>
      ) : (
        <div className="row">
          {events.map(event => (
            <div key={event._id} className="col-12 mb-4">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">{event.name}</h5>
                  <span className="badge bg-info">
                    {event.dates.length} Termine
                  </span>
                </div>
                <div className="card-body">
                  {event.dates.length === 0 ? (
                    <p className="text-muted">Keine Termine vorhanden</p>
                  ) : (
                    <div className="row">
                      {event.dates.map(eventDate => {
                        const totalParticipants = eventDate.participants.length;
                        const totalWaitlist = eventDate.waitlist.length;
                        const totalPeople = totalParticipants + totalWaitlist;

                        return (
                          <div key={eventDate._id} className="col-md-6 col-lg-4 mb-3">
                            <div className="card h-100">
                              <div className="card-header">
                                <div className="d-flex justify-content-between align-items-center">
                                  <small className="fw-bold">
                                    {(() => {
                                      if (!eventDate.date) return 'Unbekannt';
                                      const date = new Date(eventDate.date);
                                      if (isNaN(date.getTime())) return 'Ungültiges Datum';
                                      return date.toLocaleDateString('de-DE', {
                                        weekday: 'short',
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      });
                                    })()}
                                  </small>
                                  <div>
                                    <span className={`badge ${eventDate.isActive ? 'bg-success' : 'bg-secondary'} me-1`}>
                                      {eventDate.isActive ? 'Aktiv' : 'Inaktiv'}
                                    </span>
                                    {totalPeople > 0 && (
                                      <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => exportParticipants(eventDate, event.name)}
                                        title="CSV Export"
                                      >
                                        <i className="bi bi-download"></i>
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="card-body">
                                <div className="mb-3">
                                  <small className="text-muted">
                                    Max. Teilnehmer: {eventDate.maxParticipants}
                                  </small>
                                </div>

                                {/* Bestätigte Teilnehmer */}
                                <div className="mb-3">
                                  <h6 className="mb-2">
                                    <i className="bi bi-check-circle text-success me-2"></i>
                                    Teilnehmer ({totalParticipants})
                                  </h6>
                                  {totalParticipants === 0 ? (
                                    <small className="text-muted">Keine Anmeldungen</small>
                                  ) : (
                                    <div className="participants-list">
                                      {eventDate.participants.map((participant, index) => (
                                        <div key={participant._id} className="d-flex justify-content-between align-items-center py-1">
                                          <span className="small">
                                            {index + 1}. {participant.user?.username || 'Unbekannt'}
                                          </span>
                                          <small className="text-muted">
                                            {formatDate(participant.bookedAt)}
                                          </small>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {/* Warteliste */}
                                {totalWaitlist > 0 && (
                                  <div>
                                    <h6 className="mb-2">
                                      <i className="bi bi-clock text-warning me-2"></i>
                                      Warteliste ({totalWaitlist})
                                    </h6>
                                    <div className="waitlist">
                                      {eventDate.waitlist.map((waitlisted, index) => (
                                        <div key={waitlisted._id} className="d-flex justify-content-between align-items-center py-1">
                                          <span className="small text-muted">
                                            {index + 1}. {waitlisted.user?.username || 'Unbekannt'}
                                          </span>
                                          <small className="text-muted">
                                            {formatDate(waitlisted.joinedWaitlistAt)}
                                          </small>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ParticipantsList;
